import { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabaseClient";
import { logEvent } from "./history";
import { useNavigate } from "react-router-dom";
import { Container, Button, Form, Card, Row, Col, Spinner, Accordion, Badge } from "react-bootstrap";
import TargetLocalesSelector from "./components/locales/TargetLocalesSelector";
import MultiFileUpload from "./MultiFileUpload"; 
import { 
  Link45deg, BoxArrowInRight, ArrowsExpand, ArrowsCollapse, 
  Clock, Image, FileText
} from "react-bootstrap-icons";

export default function NewRequest() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // --- Form Fields ---
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState(""); 
  const [projectId, setProjectId] = useState("");
  const [contentTypeId, setContentTypeId] = useState("");
  const [formatIds, setFormatIds] = useState([]); 
  const [sourceLanguageId, setSourceLanguageId] = useState("");
  const [targetLanguageIds, setTargetLanguageIds] = useState([]); 
  const [deadline, setDeadline] = useState("");
  const [wordCount, setWordCount] = useState("");
  const [duration, setDuration] = useState(""); 
  const [imageCount, setImageCount] = useState(""); 
  const [externalLinks, setExternalLinks] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [showExtraLocales, setShowExtraLocales] = useState(false);
  const [accessGranted, setAccessGranted] = useState(false);

  // --- UI States ---
  const [filteredLocales, setFilteredLocales] = useState([]);
  const [snapshot, setSnapshot] = useState({
    languages: [], projects: [], contentTypes: [], formats: []
  });

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) navigate("/");
      else { setUser(session.user); fetchSnapshot(); }
    };
    checkUser();
  }, [navigate]);

  async function fetchSnapshot() {
    setIsLoadingData(true);
    try {
      const [langs, projs, types, forms] = await Promise.all([
        supabase.from("languages").select("*").order('region', { ascending: true }),
        supabase.from("projects").select("*").order('name'),
        supabase.from("content_types").select("*").order('name'),
        supabase.from("formats").select("*").order('name'),
      ]);
      setSnapshot({ 
        languages: langs.data || [], 
        projects: projs.data || [], 
        contentTypes: types.data || [], 
        formats: forms.data || [] 
      });
      const en = langs.data?.find(l => l.code === 'en_US');
      if (en) setSourceLanguageId(en.id);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setIsLoadingData(false);
    }
  }

  // Filtering Logic for Section 2
  useEffect(() => {
    if (isLoadingData) return;
    let availableIds = snapshot.languages.map(l => l.id);

    if (projectId) {
      const p = snapshot.projects.find(x => String(x.id) === String(projectId));
      if (p?.allowed_languages?.length) availableIds = availableIds.filter(id => p.allowed_languages.includes(id));
    }
    if (contentTypeId) {
      const t = snapshot.contentTypes.find(x => String(x.id) === String(contentTypeId));
      if (t?.allowed_languages?.length) availableIds = availableIds.filter(id => t.allowed_languages.includes(id));
    }
    formatIds.forEach(fId => {
      const f = snapshot.formats.find(x => String(x.id) === String(fId));
      if (f?.allowed_languages?.length) availableIds = availableIds.filter(id => f.allowed_languages.includes(id));
    });

    setFilteredLocales(snapshot.languages.filter(l => availableIds.includes(l.id)));
    setTargetLanguageIds(prev => prev.filter(id => availableIds.includes(id)));
  }, [projectId, contentTypeId, formatIds, snapshot, isLoadingData]);

  // IMPROVED Logic for Dynamic Specification Fields (Section 3)
  const selectedType = snapshot.contentTypes.find(t => String(t.id) === String(contentTypeId))?.name?.toLowerCase() || "";
  const selectedFormats = snapshot.formats.filter(f => formatIds.includes(f.id)).map(f => f.name.toLowerCase());

  const isAudioVideo = selectedType.includes("audio") || selectedType.includes("video") || 
                       selectedFormats.some(f => ["mp4", "wav", "mp3", "srt", "mov", "avi"].some(ext => f.includes(ext)));

  const isGraphics = selectedType.includes("graphic") || selectedType.includes("dtp") || selectedType.includes("banner") ||
                     selectedFormats.some(f => ["psd", "ai", "figma", "jpg", "png", "svg", "indd"].some(ext => f.includes(ext)));

  // Reset hidden fields when categories change
  useEffect(() => {
    if (!isAudioVideo) setDuration("");
    if (!isGraphics) setImageCount("");
  }, [isAudioVideo, isGraphics]);

  const groupedLocales = useMemo(() => {
    const list = showExtraLocales ? snapshot.languages : filteredLocales;
    return list.reduce((acc, lang) => {
      const region = lang.region || "Other";
      if (!acc[region]) acc[region] = [];
      acc[region].push(lang);
      return acc;
    }, {});
  }, [snapshot.languages, filteredLocales, showExtraLocales]);

  async function submitRequest(e) {
    e.preventDefault();
    if (!user || (externalLinks && !accessGranted)) return;
    setIsSubmitting(true);
    const { data, error } = await supabase
      .from("requests")
      .insert([{
        user_id: user.id, created_by: user.id, title, description,
        project_id: projectId || null, content_type_id: contentTypeId || null,
        format_ids: formatIds, source_language: sourceLanguageId, target_languages: targetLanguageIds,
        deadline, word_count: parseInt(wordCount) || 0, 
        duration_seconds: isAudioVideo ? parseInt(duration) : null,
        image_count: isGraphics ? parseInt(imageCount) : null, 
        external_links: externalLinks, attachments, status: "submitted",
      }])
      .select()
      .single();
    if (error) { alert("Error: " + error.message); setIsSubmitting(false); }
    else {
      try {
        await logEvent({
          entityType: "request",
          entityId: data.id,
          action: "created",
          summary: "Request created",
          details: {
            title,
            project_id: projectId || null,
            content_type_id: contentTypeId || null,
            format_ids: formatIds,
            source_language: sourceLanguageId,
            target_languages: targetLanguageIds,
            deadline,
          },
          actor: user,
        });
      } finally {
        navigate("/dashboard");
      }
    }
  }

  const activeProject = snapshot.projects.find(p => String(p.id) === String(projectId));

  if (isLoadingData) return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <Container fluid className="py-5 px-lg-5" style={{ maxWidth: '1600px' }}>
      <Form onSubmit={submitRequest}>
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div>
            <h2 className="fw-bold m-0 display-6">Create Request</h2>
            <p className="text-muted">Fill in the project details below to start the translation process.</p>
          </div>
          <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => navigate("/dashboard")}>Cancel</Button>
        </div>

        <Row className="g-4">
          <Col lg={8}>
            {/* SECTION 1: SCOPE */}
            <Card className="shadow-sm border-0 rounded-4 mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4 text-primary d-flex align-items-center">
                  <Badge bg="primary" className="me-3 rounded-circle">1</Badge> Define Scope
                </h5>
                <Form.Group className="mb-4">
                  <Form.Label className="small fw-bold text-uppercase text-muted">Request Title *</Form.Label>
                  <Form.Control size="lg" className="bg-light border-0" value={title} onChange={e => setTitle(e.target.value)} required placeholder="e.g. Website Translation" />
                </Form.Group>
                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-uppercase text-muted">Project *</Form.Label>
                    <div className="d-flex align-items-center bg-light rounded-3 px-2 border">
                      {activeProject?.icon_url && <img src={activeProject.icon_url} alt="" className="me-2 rounded shadow-sm" style={{ width: '28px', height: '28px', objectFit: 'cover' }} />}
                      <Form.Select size="lg" className="bg-transparent border-0" value={projectId} onChange={e => setProjectId(e.target.value)} required>
                        <option value="">Choose Project...</option>
                        {snapshot.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </Form.Select>
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-uppercase text-muted">Content Type *</Form.Label>
                    <Form.Select size="lg" className="bg-light border-0 border" value={contentTypeId} onChange={e => setContentTypeId(e.target.value)} required>
                      <option value="">Choose Type...</option>
                      {snapshot.contentTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </Form.Select>
                  </Col>
                </Row>
                <Form.Label className="small fw-bold text-uppercase text-muted">Expected Formats</Form.Label>
                <div className="p-3 bg-light rounded-4 border border-dashed">
                  <Row className="g-2">
                    {snapshot.formats.map(f => (
                      <Col xs={6} md={3} key={f.id}>
                        <Form.Check type="checkbox" label={f.name} id={`f-${f.id}`} checked={formatIds.includes(f.id)} onChange={() => setFormatIds(prev => prev.includes(f.id) ? prev.filter(id => id !== f.id) : [...prev, f.id])} />
                      </Col>
                    ))}
                  </Row>
                </div>
              </Card.Body>
            </Card>

            <TargetLocalesSelector
  groupedLocales={groupedLocales}
  filteredLocales={filteredLocales}
  targetLanguageIds={targetLanguageIds}
  setTargetLanguageIds={setTargetLanguageIds}
  showExtraLocales={showExtraLocales}
  setShowExtraLocales={setShowExtraLocales}
/>

            {/* SECTION 3: SPECIFICATIONS */}
            <Card className="shadow-sm border-0 rounded-4 mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4 text-primary d-flex align-items-center">
                  <Badge bg="primary" className="me-3 rounded-circle">3</Badge> Specifications
                </h5>
                <Row className="g-4">
                  <Col md={12}>
                    <Form.Label className="small fw-bold text-uppercase text-muted">Description *</Form.Label>
                    <Form.Control as="textarea" rows={4} className="bg-light border-0" value={description} onChange={e => setDescription(e.target.value)} required placeholder="Provide clear instructions for the linguists..." />
                  </Col>
                  
                  <Col md={4}>
                    <Form.Label className="small fw-bold text-uppercase text-muted">Deadline *</Form.Label>
                    <Form.Control type="datetime-local" className="bg-light border-0 border" value={deadline} onChange={e => setDeadline(e.target.value)} required />
                  </Col>

                  {isGraphics && (
                    <Col md={4}>
                      <Form.Label className="small fw-bold text-uppercase text-muted"><Image className="me-1"/> Total Images *</Form.Label>
                      <Form.Control type="number" className="bg-light border-0 border" value={imageCount} onChange={e => setImageCount(e.target.value)} required min="1" placeholder="Quantity" />
                    </Col>
                  )}

                  {isAudioVideo && (
                    <Col md={4}>
                      <Form.Label className="small fw-bold text-uppercase text-muted"><Clock className="me-1"/> Duration (Sec) *</Form.Label>
                      <Form.Control type="number" className="bg-light border-0 border" value={duration} onChange={e => setDuration(e.target.value)} required min="1" placeholder="Total seconds" />
                    </Col>
                  )}

                  <Col md={4}>
                    <Form.Label className="small fw-bold text-uppercase text-muted"><FileText className="me-1"/> Word Count *</Form.Label>
                    <Form.Control type="number" className="bg-light border-0 border" value={wordCount} onChange={e => setWordCount(e.target.value)} required min="1" placeholder="Source words" />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* SECTION 4: ASSETS & LINKS */}
            <Card className="shadow-sm border-0 rounded-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4 text-primary d-flex align-items-center">
                  <Badge bg="primary" className="me-3 rounded-circle">4</Badge> Assets & Links
                </h5>
                <div className="p-4 bg-light rounded-4 border mb-4">
                  <Form.Label className="small fw-bold text-uppercase text-muted"><Link45deg/> Cloud Reference Links</Form.Label>
                  <Form.Control as="textarea" rows={2} className="border-0 shadow-sm mb-3" value={externalLinks} onChange={e => setExternalLinks(e.target.value)} placeholder="Figma, Google Drive, etc..." />
                  {externalLinks && (
                    <div className="d-flex align-items-center p-3 bg-danger bg-opacity-10 rounded-3 border border-danger border-opacity-25">
                      <Form.Check type="checkbox" className="small fw-bold text-danger" label="Access shared with managers@prasae.xyz" checked={accessGranted} onChange={e => setAccessGranted(e.target.checked)} required />
                    </div>
                  )}
                </div>
                <MultiFileUpload onFilesUpdated={setAttachments} />
              </Card.Body>
            </Card>
          </Col>

          {/* SIDEBAR SUMMARY */}
          <Col lg={4}>
            <div className="sticky-top" style={{ top: '100px', zIndex: 10 }}>
              <Card className="shadow-sm border-0 rounded-4 overflow-hidden border">
                <div className="bg-primary p-4 text-white">
                  <h5 className="fw-bold mb-0">Summary View</h5>
                  <small className="opacity-75">Review before submitting</small>
                </div>
                <Card.Body className="p-4">
                  <div className="mb-4">
                      <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted small">Target Locales</span>
                          <Badge bg="primary" pill className="px-3">{targetLanguageIds.length}</Badge>
                      </div>
                      <div className="d-flex justify-content-between mb-3 border-top pt-3">
                          <span className="text-muted small">Word Count</span>
                          <span className="fw-bold">{wordCount || "-"}</span>
                      </div>
                      {isAudioVideo && (
                        <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted small">Duration</span>
                            <span className="fw-bold">{duration ? `${duration}s` : "-"}</span>
                        </div>
                      )}
                      {isGraphics && (
                        <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted small">Images</span>
                            <span className="fw-bold">{imageCount || "-"}</span>
                        </div>
                      )}
                  </div>
                  <Button type="submit" variant="success" size="lg" className="w-100 py-3 fw-bold shadow-lg rounded-pill" disabled={isSubmitting || targetLanguageIds.length === 0}>
                    {isSubmitting ? <Spinner size="sm"/> : <><BoxArrowInRight className="me-2"/> Submit Request</>}
                  </Button>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Form>

      <style>{`
        .custom-scroll-container { max-height: 480px; overflow-y: auto; }
        .hover-bg:hover { background-color: #f1f8ff !important; }
        .border-dashed { border: 2px dashed #dee2e6 !important; }
        .accordion-button:not(.collapsed) { background-color: transparent; color: inherit; box-shadow: none; }
      `}</style>
    </Container>
  );
}